/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DebugOverlayMode } from "../../model/videoDebugOverlay";
import type { TDebugMetricGroup } from "../../model/videoDebugOverlay";

/**
 * Redux state for the Video Debug Overlay.
 *
 * Holds the *projected* rows rather than the raw snapshot: the projection
 * depends on the mode, which lives here, so doing it on the way in keeps
 * the component a pure renderer and lets the clipboard export reuse
 * exactly what is on screen.
 */
export interface VideoDebugOverlayState {
  /** Rows currently on screen, already filtered and formatted. */
  groups: ReadonlyArray<TDebugMetricGroup>;

  /**
   * The persisted QA preference. Independent of {@link isVisible}: the key
   * sequence and the playback menu toggle visibility for this session
   * only, and must not silently rewrite a preference the user set.
   */
  isPersistedEnabled: boolean;

  /** Whether the panel is on screen. */
  isVisible: boolean;

  /** `Date.now()` of the most recent snapshot; 0 when none taken. */
  lastUpdatedAtMs: number;

  /** How much of the metric set to render. */
  mode: DebugOverlayMode;
}

/** Payload for the `metricsUpdated` reducer. */
export interface VideoDebugMetricsPayload {
  /** Freshly projected rows. */
  groups: ReadonlyArray<TDebugMetricGroup>;

  /** `Date.now()` at which the snapshot was taken. */
  timestampMs: number;
}

/** Payload for the `visibilityUpdated` reducer. */
export interface VideoDebugVisibilityPayload {
  /** Whether the panel should be on screen. */
  isVisible: boolean;

  /** Mode to apply alongside the visibility change. */
  mode: DebugOverlayMode;
}

export const INITIAL_STATE: VideoDebugOverlayState = {
  groups: [],
  isPersistedEnabled: false,
  isVisible: false,
  lastUpdatedAtMs: 0,
  mode: DebugOverlayMode.COMPACT
};
